import { ActionIcon } from '@mantine/core';
import { IconSettings } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { canViewAdmin } from '~api';
import { api } from '~portal/api';
import { Cmp, logger } from '~portal/lib';
import { touchTarget } from '~portal/styles/touch-target.css';

/**
 * @desc The admin entry point: a cog that renders for an administrator and nothing at all for
 * anyone else.
 *
 * A component rather than a node built at one call site, because the chrome it belongs to differs
 * by surface — the chat's floating cluster on a desktop and the chat history drawer's header on a
 * phone, on both builds — and those surfaces are in different modules. The platform global nav
 * no longer carries it: its "Admin" item replaces the cog there (add-ario-identity-and-authoring-
 * round-3 D4). Sharing the *component* keeps one place that knows the permission check,
 * the label and the destination.
 */
export const AdminSettingsButton: Cmp = function () {
  const navigate = useNavigate();
  const profileQuery = api.users.me.useQuery();

  if (!canViewAdmin(profileQuery.data)) return null;

  return (
    <ActionIcon
      onClick={() => {
        navigate({ to: '/admin/users' }).catch((err) => {
          logger.error('[AdminSettingsButton] Navigation to admin failed', {
            error: err instanceof Error ? err.message : String(err),
          });
        });
      }}
      size="lg"
      variant="default"
      className={touchTarget}
      aria-label="Admin settings"
      data-dd-action-name="Admin settings"
    >
      <IconSettings size={20} />
    </ActionIcon>
  );
};
